/**
 * Support inbox — where the team answers the widget that ships in telescope.
 *
 * Reads from the support service (intercom.brainhi.com), never from cosmos.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import axios, { AxiosResponse } from 'axios';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import {
  createLabel,
  createMacro,
  deleteMacro,
  fetchContactConversations,
  fetchConversation,
  fetchConversations,
  fetchLabels,
  fetchMacros,
  sendNote,
  sendReply,
  setConversationLabels,
  startConversation,
  SUPPORT_URL,
  updateConversation,
  updateLabel,
  updateMacro,
  uploadAttachment,
} from './api';
import { ConversationList, Thread } from './components';
import Analytics from './components/Analytics';
import LabelModal from './components/LabelModal';
import MacroModal from './components/MacroModal';
import NewConversationModal from './components/NewConversationModal';
import {
  Conversation,
  ConversationDetail,
  ConversationState,
  Label,
  Macro,
  Message,
} from './types';
import useNotificationSound from './useNotificationSound';
import useSupportStream from './useSupportStream';

// A payload from a support service that predates labels or unread comes
// without the field. Normalized at the boundary so the chips, the close guard
// and the row styling can rely on it, whatever is deployed on the other side.
const withDefaults = <T extends Conversation>(conversation: T): T => ({
  ...conversation,
  labels: conversation.labels ?? [],
  unread: conversation.unread ?? false,
  // A service without the field can still say a customer wrote and nobody
  // looked; that is the closest it has to "still owes an answer".
  awaiting_reply: conversation.awaiting_reply ?? conversation.unread ?? false,
  rating: conversation.rating ?? null,
  rated_at: conversation.rated_at ?? null,
  closed_at: conversation.closed_at ?? null,
});

type View = 'inbox' | 'analytics';

const Support: React.FC = () => {
  const [view, setView] = useState<View>('inbox');
  const [state, setState] = useState<ConversationState>('open');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<ConversationDetail | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);
  // Bumped to retry a load that failed: clicking the same row again doesn't
  // change selectedId, so it needs something else to re-run the effect.
  const [retry, setRetry] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [macros, setMacros] = useState<Macro[]>([]);
  const [isMacroModalOpen, setIsMacroModalOpen] = useState(false);
  const [labels, setLabels] = useState<Label[]>([]);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [history, setHistory] = useState<Conversation[]>([]);
  const labelsRequest = useRef(0);
  // Mirrors the team list for the async paths below: after an await, a
  // closure's copy of `labels` predates a label created in the meantime.
  const labelsRef = useRef<Label[]>(labels);
  labelsRef.current = labels;
  const playNotificationSound = useNotificationSound();

  // Search puts several list requests in flight; only the newest one may
  // paint, or "riv" answers after "rivera" and the rows stop matching the
  // input. A ticket rather than an effect-scoped flag because loadList is
  // also called imperatively (stream, replies, labels).
  const listRequest = useRef(0);

  const loadList = useCallback(async (): Promise<void> => {
    const ticket = ++listRequest.current;
    try {
      // A search spans both tabs: who the agent is looking for matters more
      // than where their conversations sit.
      const term = search.trim();
      const { data } = await fetchConversations(term ? 'all' : state, term);
      if (ticket === listRequest.current) setConversations(data.map(withDefaults));
    } catch {
      if (ticket === listRequest.current) toast.error('Could not load conversations.');
    } finally {
      if (ticket === listRequest.current) setIsLoadingList(false);
    }
  }, [state, search]);

  useEffect(() => {
    // Debounced for typing in the search box, with the spinner armed inside
    // the timeout: flipping it per keystroke would replace the rows with
    // "Loading…" — and announce it — on every character.
    const handle = window.setTimeout(() => {
      setIsLoadingList(true);
      void loadList();
    }, 250);
    return () => window.clearTimeout(handle);
  }, [loadList]);

  const loadMacros = useCallback(async (): Promise<void> => {
    try {
      const { data } = await fetchMacros();
      setMacros(data);
    } catch {
      // The composer works without them; the picker just comes up empty.
    }
  }, []);

  useEffect(() => {
    void loadMacros();
  }, [loadMacros]);

  const loadLabels = useCallback(async (): Promise<void> => {
    try {
      const { data } = await fetchLabels();
      setLabels(data);
    } catch {
      // The picker just comes up empty; assigning still works once it loads.
    }
  }, []);

  useEffect(() => {
    void loadLabels();
  }, [loadLabels]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return undefined;
    }

    // The thread on screen must always be the selected one. Holding on to the
    // previous conversation while this one loads — or forever, if the load
    // fails — painted A's messages under B's highlighted row, and the composer
    // posted to B while showing A: the "mixed conversations" that a reload
    // fixed. Nothing is better than the wrong thing.
    setSelected((current) => (current && current.id === selectedId ? current : null));
    setFailedId(null);

    // Guarded because the responses can land out of order: clicking A then B
    // with A's response arriving second would leave the thread showing A while
    // every reply and status change targeted B. The same stale write would also
    // clobber anything the stream appended in between.
    let active = true;
    const load = async (): Promise<void> => {
      try {
        const { data } = await fetchConversation(selectedId);
        if (!active) return;
        setSelected(withDefaults(data));
        // The fetch itself recorded the read; the list only learns on its next
        // refresh, and the row must not stay bold once the agent moves on.
        setConversations((current) =>
          current.map((row) => (row.id === data.id ? { ...row, unread: false } : row))
        );
      } catch {
        if (!active) return;
        setFailedId(selectedId);
        toast.error('Could not load that conversation.');
      }
    };
    void load();

    return () => {
      active = false;
    };
  }, [selectedId, retry]);

  const select = (id: string): void => {
    if (id === selectedId && id === failedId) setRetry((n) => n + 1);
    setSelectedId(id);
  };

  // Keyed on the contact rather than the conversation: appending messages to
  // the open thread must not refetch the history panel.
  const selectedContactId = selected?.contact.id ?? null;
  useEffect(() => {
    // Emptied first: the previous contact's conversations must not sit under
    // this contact's name while theirs load.
    setHistory([]);
    if (!selectedContactId) return undefined;
    let active = true;
    fetchContactConversations(selectedContactId)
      .then(({ data }) => {
        if (active) setHistory(data.map(withDefaults));
      })
      .catch(() => {
        // The thread works without its history panel.
      });
    return () => {
      active = false;
    };
  }, [selectedContactId]);

  // Append rather than refetch so an agent reading a long thread doesn't get
  // yanked back to the server's idea of it mid-scroll. One function for the
  // stream and every send path, so the dedup — against whichever of the two
  // landed the message first — can't drift between call sites.
  const appendMessage = (message: Message): void => {
    setSelected((current) => {
      if (!current || current.id !== message.conversation_id) return current;
      if (current.messages.some((existing) => existing.id === message.id)) return current;
      return { ...current, messages: [...current.messages, message] };
    });
  };

  // Opening a thread is what records it as read on the server, so a customer
  // message landing in the one already open is marked the same way — or its
  // row would go bold the moment the agent moves on. Held while the tab is in
  // the background: reading also tells the customer they were seen.
  const pendingRead = useRef<string | null>(null);

  const markRead = useCallback(
    async (id: string): Promise<void> => {
      if (document.visibilityState !== 'visible') {
        pendingRead.current = id;
        return;
      }
      pendingRead.current = null;
      try {
        await fetchConversation(id);
        void loadList();
      } catch {
        // Stays unread; the next open records it.
      }
    },
    [loadList]
  );

  useEffect(() => {
    const onVisible = (): void => {
      const id = pendingRead.current;
      if (id && id === selectedId) void markRead(id);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [markRead, selectedId]);

  useSupportStream(selectedId, {
    onMessage: (message) => {
      appendMessage(message);
      if (message.author_type === 'contact' && message.conversation_id === selectedId) {
        void markRead(message.conversation_id);
      }
    },
    onInboxChanged: (event) => {
      // Ring for customers only: telescope's rule (sender === PATIENT). A
      // colleague replying or closing a thread also lands here and must not
      // sound like new work.
      if (event.author_type === 'contact') playNotificationSound();
      // A rating changes nothing in the thread but its header; patched in
      // place rather than refetching the whole conversation.
      const { rating } = event;
      if (typeof rating === 'number' && event.conversation_id) {
        setSelected((current) =>
          current && current.id === event.conversation_id ? { ...current, rating } : current
        );
      }
      void loadList();
    },
  });

  // One delivery path for everything the composer produces: post it, append
  // it deduplicated against what the stream may have landed first, give the
  // failure its own words. Notes skip the list refresh because they don't
  // move the queue.
  const deliver = async (
    post: (id: string, body: string) => Promise<AxiosResponse<Message>>,
    body: string,
    failureMessage: string,
    refreshList: boolean
  ): Promise<void> => {
    // Only ever into the thread on screen.
    if (!selectedId || selected?.id !== selectedId) return;
    setIsSending(true);
    try {
      const { data } = await post(selectedId, body);
      appendMessage(data);
      if (refreshList) void loadList();
    } catch (error) {
      toast.error(failureMessage);
      throw error;
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = (body: string): Promise<void> =>
    deliver(sendReply, body, 'The reply did not go through.', true);

  const handleSendNote = (body: string): Promise<void> =>
    deliver(sendNote, body, 'The note was not saved.', false);

  const handleSendFile = async (file: File): Promise<void> => {
    if (!selectedId || selected?.id !== selectedId) return;
    setIsSending(true);
    try {
      const { data } = await uploadAttachment(selectedId, file);
      appendMessage(data);
      void loadList();
    } catch (error) {
      // The two refusals the server hands out get their own words; the file
      // picker's accept filter makes them rare, not impossible.
      if (axios.isAxiosError(error) && error.response?.status === 413) {
        toast.error('That file is too large.');
      } else if (axios.isAxiosError(error) && error.response?.status === 415) {
        toast.error('Only images, videos and PDFs can be sent.');
      } else {
        toast.error('The file did not go through.');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleToggleState = async (): Promise<void> => {
    if (!selected) return;
    const next: ConversationState = selected.state === 'open' ? 'closed' : 'open';
    // Closing files the conversation, so filing is required — same rule the
    // server enforces; checking here just saves the round trip. Only once the
    // labels endpoint has answered, though: against a service that predates
    // labels this guard would otherwise block closing with a picker that can
    // never be filled, and the server stays the authority either way.
    if (next === 'closed' && labels.length > 0 && selected.labels.length === 0) {
      toast.error('Add at least one label before closing.');
      return;
    }
    try {
      await updateConversation(selected.id, { state: next });
      setSelected({ ...selected, state: next });
      // A conversation that just left this tab should stop being selected.
      if (next !== state) setSelectedId(null);
      void loadList();
    } catch (error) {
      // The server refuses an unlabeled close too (a colleague may have just
      // stripped the labels); its 422 deserves the same words as our check.
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        toast.error('Add at least one label before closing.');
      } else {
        toast.error('Could not change the status.');
      }
    }
  };

  const handleSetLabels = async (labelIds: string[]): Promise<void> => {
    if (!selected) return;
    // Rapid toggles put several PUTs in flight; only the newest one may write
    // back, or an older response repaints a selection the agent already moved
    // past. Same discipline as the fetchConversation effect above.
    const ticket = ++labelsRequest.current;

    // Written optimistically too: the close guard reads selected.labels, and
    // a label the agent just applied has to count there while its PUT is
    // still in flight — bouncing the close with the chip on screen would read
    // as a lie. Resolved against the team list plus whatever the conversation
    // already carried (archived labels have left the team list).
    const known = new Map(
      // Through the ref: create-and-assign calls in right after createLabel,
      // and dropping the just-created id here would uncheck the chip and
      // bounce the close until the PUT lands.
      [...selected.labels, ...labelsRef.current].map((label) => [label.id, label] as const)
    );
    const previous = selected.labels;
    const optimistic = labelIds.flatMap((id) => {
      const label = known.get(id);
      return label === undefined ? [] : [label];
    });
    setSelected((current) =>
      current && current.id === selected.id ? { ...current, labels: optimistic } : current
    );

    try {
      const { data } = await setConversationLabels(selected.id, labelIds);
      if (ticket !== labelsRequest.current) return;
      setSelected((current) =>
        current && current.id === data.id ? { ...current, labels: data.labels ?? [] } : current
      );
      void loadList();
    } catch (error) {
      if (ticket === labelsRequest.current) {
        setSelected((current) =>
          current && current.id === selected.id ? { ...current, labels: previous } : current
        );
      }
      toast.error('Could not update the labels.');
      throw error;
    }
  };

  const handleCreateLabel = async (name: string): Promise<Label> => {
    try {
      const { data } = await createLabel({ name });
      // Into the list right away: the optimistic path resolves ids against
      // this state, and the created label must count before the refetch
      // lands. The background reload then brings the server's ordering.
      setLabels((current) =>
        current.some((label) => label.id === data.id) ? current : [...current, data]
      );
      void loadLabels();
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        toast.error('A label with that name already exists.');
      } else {
        toast.error('Could not create the label.');
      }
      throw error;
    }
  };

  const handleStartConversation = async (
    accountId: string,
    userId: string,
    body: string
  ): Promise<void> => {
    try {
      const { data } = await startConversation({ account_id: accountId, user_id: userId, body });
      setIsNewConversationOpen(false);
      // Straight into the thread, wherever the list was: the conversation is
      // open whether it was just created or the person already had one.
      setSearch('');
      setState('open');
      setSelected(withDefaults(data));
      setSelectedId(data.id);
      void loadList();
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404) toast.error('That user is no longer part of the account.');
      else if (status === 422) toast.error('That user is deactivated.');
      else toast.error('The message did not go through.');
      throw error;
    }
  };

  if (!SUPPORT_URL) {
    return (
      <div className="p-6 text-gray-1.0">
        The support service is not configured. Set REACT_APP_SUPPORT_URL and rebuild.
      </div>
    );
  }

  const openFromAnalytics = (id: string): void => {
    setView('inbox');
    select(id);
  };

  return (
    <div className="flex flex-col h-full">
      <div
        className="flex flex-shrink-0 gap-4 px-4 bg-white-0.0 border-b border-white-2.0"
        role="tablist"
        aria-label="Support view"
      >
        {(['inbox', 'analytics'] as View[]).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={view === option}
            onClick={() => setView(option)}
            className={classNames(
              'py-2 text-sm font-medium capitalize border-b-4 transition ease-in-out duration-200',
              view === option
                ? 'border-primary-2.0 text-primary-2.0'
                : 'border-white-0.0 text-gray-2.0 hover:text-primary-2.0'
            )}
          >
            {option}
          </button>
        ))}
      </div>

      {view === 'analytics' ? (
        <Analytics onOpenConversation={openFromAnalytics} />
      ) : (
        <div className="flex flex-1 min-h-0">
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            state={state}
            isLoading={isLoadingList}
            search={search}
            onSearchChange={setSearch}
            onSelect={select}
            onStateChange={(next) => {
              setState(next);
              setSelectedId(null);
            }}
            onNewConversation={() => setIsNewConversationOpen(true)}
          />

          {selected ? (
            <Thread
              conversation={selected}
              history={history.filter((conversation) => conversation.id !== selected.id)}
              isSending={isSending}
              labels={labels}
              macros={macros}
              onOpenConversation={select}
              onSend={handleSend}
              onSendNote={handleSendNote}
              onSendFile={handleSendFile}
              onSetLabels={handleSetLabels}
              onCreateLabel={handleCreateLabel}
              onManageLabels={() => setIsLabelModalOpen(true)}
              onToggleState={handleToggleState}
              onManageMacros={() => setIsMacroModalOpen(true)}
            />
          ) : (
            <div className="items-center justify-center flex-1 hidden md:flex text-gray-1.0">
              {!selectedId && 'Pick a conversation.'}
              {selectedId && selectedId === failedId && 'Could not load that conversation.'}
              {selectedId && selectedId !== failedId && 'Loading…'}
            </div>
          )}
        </div>
      )}

      <NewConversationModal
        isOpen={isNewConversationOpen}
        onClose={() => setIsNewConversationOpen(false)}
        onStart={handleStartConversation}
      />

      <LabelModal
        isOpen={isLabelModalOpen}
        labels={labels}
        onClose={() => setIsLabelModalOpen(false)}
        onCreate={async (name) => {
          await handleCreateLabel(name);
        }}
        onRename={async (id, name) => {
          try {
            const { data } = await updateLabel(id, { name });
            await loadLabels();
            // The rename also shows on the open thread's chips right away.
            setSelected((current) =>
              current
                ? {
                    ...current,
                    labels: current.labels.map((label) => (label.id === data.id ? data : label)),
                  }
                : current
            );
            void loadList();
          } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 409) {
              toast.error('A label with that name already exists.');
            } else {
              toast.error('Could not rename the label.');
            }
            throw error;
          }
        }}
        onSetArchived={async (id, archived) => {
          try {
            await updateLabel(id, { archived });
            await loadLabels();
          } catch (error) {
            toast.error('Could not update the label.');
            throw error;
          }
        }}
      />

      <MacroModal
        isOpen={isMacroModalOpen}
        macros={macros}
        onClose={() => setIsMacroModalOpen(false)}
        onCreate={async (title, body) => {
          // Re-thrown so the modal keeps the text instead of resetting a form
          // whose save just failed — same contract as the composer's onSend.
          try {
            await createMacro({ title, body });
            await loadMacros();
          } catch (error) {
            toast.error('Could not save the quick reply.');
            throw error;
          }
        }}
        onUpdate={async (id, title, body) => {
          try {
            await updateMacro(id, { title, body });
            await loadMacros();
          } catch (error) {
            toast.error('Could not save the quick reply.');
            throw error;
          }
        }}
        onDelete={async (id) => {
          try {
            await deleteMacro(id);
            await loadMacros();
          } catch (error) {
            toast.error('Could not delete the quick reply.');
            throw error;
          }
        }}
      />
    </div>
  );
};

export default Support;
